import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { ArrowLeft, Plus, Trophy, Trash2, Users, Clock, Calendar } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || (import.meta && import.meta.env && import.meta.env.REACT_APP_BACKEND_URL);

export default function Tournaments() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tournaments, setTournaments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [tab, setTab] = useState('list'); // 'list' | 'ranking'
  const [overallRanking, setOverallRanking] = useState([]);
  const [rankingLoading, setRankingLoading] = useState(false);

  useEffect(() => {
    loadTournaments();
  }, []);

  useEffect(() => {
    if (tab === 'ranking') {
      loadOverallRanking();
    }
  }, [tab]);

  const loadTournaments = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/tournaments/list`);
      const data = await response.json();
      if (data.ok) {
        setTournaments(data.tournaments);
      }
    } catch (error) {
      console.error('Failed to load tournaments:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadOverallRanking = async () => {
    setRankingLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/tournaments/overall-ranking`);
      const data = await response.json();
      if (data.ok) {
        setOverallRanking(data.rankings);
      }
    } catch (error) {
      console.error('Failed to load overall ranking:', error);
    } finally {
      setRankingLoading(false);
    }
  };

  const deleteTournament = async (tournamentId) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/tournaments/${tournamentId}?user_id=${user.uid}`, {
        method: 'DELETE'
      });

      const data = await response.json();

      if (!response.ok) {
        alert(`Fehler: ${data.detail || data.message || 'Unbekannter Fehler'}`);
        return;
      }
      
      if (data.ok) {
        alert('Turnier gelöscht');
        loadTournaments();
      }
    } catch (error) {
      console.error('Failed to delete tournament:', error);
      alert(`Fehler beim Löschen: ${error.message}`);
    } finally {
      setDeleteConfirm(null);
    }
  };

  const getStatusBadge = (status) => {
    const colors = {
      draft: 'bg-gray-500/20 text-gray-300 border-gray-500/30',
      running: 'bg-green-500/20 text-green-300 border-green-500/30',
      finished: 'bg-blue-500/20 text-blue-300 border-blue-500/30'
    };
    const labels = {
      draft: 'Entwurf',
      running: 'Läuft',
      finished: 'Beendet'
    };
    return (
      <span className={`px-2 py-0.5 rounded-full text-xs border ${colors[status] || 'bg-gray-500/20 text-gray-300'}`}>
        {labels[status] || status}
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-[#0a0608] text-rose-50 pb-24">
      {/* Header */}
      <header className="px-4 py-3 bg-[#12090c] border-b border-white/5 sticky top-0 z-10">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/')} className="p-1">
              <ArrowLeft size={20} />
            </button>
            <div>
              <h1 className="font-semibold flex items-center gap-2">
                <Trophy size={18} className="text-purple-400" />
                Turniere
              </h1>
            </div>
          </div>
          <Button 
            onClick={() => navigate('/tournaments/new')}
            className="bg-purple-600 hover:bg-purple-700 text-sm px-3 py-1.5 h-auto"
          >
            <Plus size={14} className="mr-1" /> Neu
          </Button>
        </div>
      </header>

      <div className="px-3 py-4 space-y-4">
        {/* Tabs */}
        <div className="flex gap-1 bg-[#12090c] p-1 rounded-lg border border-white/5">
          <button 
            onClick={() => setTab('list')}
            className={`flex-1 py-2 px-3 text-sm rounded-md transition-colors ${
              tab === 'list' 
                ? 'bg-purple-600 text-white' 
                : 'text-rose-200/70 hover:bg-white/5'
            }`}
          >
            Alle Turniere
          </button>
          <button 
            onClick={() => setTab('ranking')}
            className={`flex-1 py-2 px-3 text-sm rounded-md transition-colors ${
              tab === 'ranking' 
                ? 'bg-purple-600 text-white' 
                : 'text-rose-200/70 hover:bg-white/5'
            }`}
          >
            Gesamtwertung
          </button>
        </div>

        {/* Tab: Gesamtwertung */}
        {tab === 'ranking' && (
          <div>
            {rankingLoading ? (
              <div className="text-center py-8 text-rose-200/50">Lade Gesamtwertung...</div>
            ) : overallRanking.length === 0 ? (
              <Card className="bg-[#12090c] border-white/5">
                <CardContent className="py-6 text-center">
                  <Trophy size={32} className="mx-auto mb-3 text-rose-200/30" />
                  <p className="text-rose-200/60">Noch keine Turnierergebnisse</p>
                  <p className="text-rose-200/40 text-sm mt-1">
                    Beende ein Turnier, um die Gesamtwertung zu starten.
                  </p>
                </CardContent>
              </Card>
            ) : (
              <Card className="bg-[#12090c] border-white/5">
                <CardContent className="pt-4">
                  <p className="text-xs text-rose-200/40 mb-3">
                    Punkte basierend auf Platzierung
                  </p>
                  
                  {/* Table Header */}
                  <div className="grid grid-cols-12 gap-1 text-xs text-rose-200/50 pb-2 border-b border-white/10 mb-2">
                    <div className="col-span-1">#</div>
                    <div className="col-span-5">Spieler</div>
                    <div className="col-span-2 text-center">Pkt</div>
                    <div className="col-span-2 text-center">Turn.</div>
                    <div className="col-span-2 text-center">S/U/N</div>
                  </div>
                  
                  {/* Rankings */}
                  <div className="space-y-1">
                    {overallRanking.map((player, index) => {
                      const position = index + 1;
                      const medal = position === 1 ? '🥇' : position === 2 ? '🥈' : position === 3 ? '🥉' : '';
                      const pointsColor = player.total_ranking_points > 0 
                        ? 'text-green-400' 
                        : player.total_ranking_points < 0 
                          ? 'text-red-400' 
                          : 'text-rose-200';
                      
                      return (
                        <div 
                          key={player.id}
                          className={`grid grid-cols-12 gap-1 text-sm py-2 px-1 rounded ${
                            position <= 3 ? 'bg-gradient-to-r from-yellow-900/20 to-transparent' : ''
                          }`}
                        >
                          <div className="col-span-1 text-rose-200/50">
                            {medal || position}
                          </div>
                          <div className="col-span-5 font-medium truncate">
                            {player.player_name}
                          </div>
                          <div className={`col-span-2 text-center font-bold ${pointsColor}`}>
                            {player.total_ranking_points > 0 ? '+' : ''}{player.total_ranking_points}
                          </div>
                          <div className="col-span-2 text-center text-rose-200/60">
                            {player.tournaments_played}
                          </div>
                          <div className="col-span-2 text-center text-rose-200/60 text-xs">
                            {player.total_wins}/{player.total_draws}/{player.total_losses}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  
                  {/* Legend */}
                  <div className="mt-4 pt-3 border-t border-white/5 text-xs text-rose-200/30">
                    <div className="flex gap-4 flex-wrap">
                      <span>Pkt = Ranking-Punkte</span>
                      <span>Turn. = Teilnahmen</span>
                      <span>S/U/N = Siege/Unent./Nieder.</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Tab: Tournaments List */}
        {tab === 'list' && (
          <>
            {loading && (
              <div className="text-center py-8 text-rose-200/50">Lade Turniere...</div>
            )}

            {!loading && tournaments.length === 0 && (
              <Card className="bg-[#12090c] border-white/5">
                <CardContent className="py-6 text-center">
                  <Trophy size={32} className="mx-auto mb-3 text-rose-200/30" />
                  <p className="text-rose-200/60 mb-4">Noch keine Turniere vorhanden</p>
                  <Button 
                    onClick={() => navigate('/tournaments/new')}
                    className="bg-purple-600 hover:bg-purple-700"
                  >
                    Erstes Turnier erstellen
                  </Button>
                </CardContent>
              </Card>
            )}

            {!loading && tournaments.length > 0 && (
              <div className="space-y-2">
                {tournaments.map(tournament => (
                  <Card 
                    key={tournament.id}
                    className="bg-[#12090c] border-white/5 overflow-hidden"
                  >
                    <CardContent className="p-0">
                      <div 
                        className="p-3 cursor-pointer hover:bg-white/5 transition-colors"
                        onClick={() => navigate(`/tournaments/${tournament.id}`)}
                      >
                        <div className="flex justify-between items-start mb-2">
                          <h3 className="font-medium truncate flex-1 mr-2">
                            {tournament.name}
                          </h3>
                          {getStatusBadge(tournament.status)}
                        </div>
                        
                        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-rose-200/50">
                          <span className="flex items-center gap-1">
                            <Users size={12} />
                            {tournament.courts} Plätze
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock size={12} />
                            {tournament.round_duration_seconds / 60} Min/Runde
                          </span>
                          <span className="flex items-center gap-1">
                            <Calendar size={12} />
                            {new Date(tournament.created_at?.seconds * 1000 || tournament.created_at).toLocaleDateString('de-DE')}
                          </span>
                        </div>
                      </div>

                      {/* Delete Button */}
                      <div className="px-3 pb-3">
                        <button 
                          onClick={(e) => {
                            e.stopPropagation();
                            setDeleteConfirm(tournament.id);
                          }}
                          className="w-full py-2 text-xs text-red-400/70 hover:text-red-400 hover:bg-red-500/10 rounded-lg border border-red-500/20 transition-colors flex items-center justify-center gap-1"
                        >
                          <Trash2 size={12} />
                          Löschen
                        </button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <Card className="bg-[#12090c] border-red-500/30 max-w-sm w-full">
            <CardContent className="pt-5">
              <h3 className="font-semibold mb-2">Turnier löschen?</h3>
              <p className="text-sm text-rose-200/60 mb-4">
                Alle Daten werden unwiderruflich gelöscht!
              </p>
              <div className="flex gap-2">
                <Button 
                  onClick={() => setDeleteConfirm(null)}
                  variant="outline"
                  className="flex-1 bg-white/5 border-white/10"
                >
                  Abbrechen
                </Button>
                <Button 
                  onClick={() => deleteTournament(deleteConfirm)}
                  className="flex-1 bg-red-600 hover:bg-red-700"
                >
                  Löschen
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}